/**
 * Foundation Data v2 rows, in the shape a curation session works in.
 *
 * The extracted rows arrive from Java as `FoundationDataPointDto` (`dataKey`, `dataValue`, `sourcePage`,
 * …) and the curated destination's fields are named `key`, `value`, `page`, … — the wire vocabulary, which
 * differs only because `key` and `value` are reserved SQL words. The session's frame must use the
 * DESTINATION's names, because the save matches frame columns to destination fields by name: a frame of
 * `dataKey`/`dataValue` would map nothing and the agent would have to rename every column before it could
 * do any actual work.
 *
 * `id` becomes `sourcePointId`, so lineage back to the raw row is carried without the agent doing
 * anything. `valueNumeric` is deliberately dropped — Java derives it from `value` on write, and a column
 * the agent could edit would let the number disagree with the text it came from.
 */

/**
 * Every field of the curated destination, in the order they should appear as columns.
 *
 * This list must match FOUNDATION_DESCRIPTOR's field names in
 * `data-sorting/backend/app/services/foundation_reference.py`. The two sit either side of a service
 * boundary with no compiler between them, and the save matches frame columns to destination fields BY
 * NAME — so a rename on one side does not error, it silently maps nothing. Both sides pin the list as an
 * explicit literal in their own test, so changing either forces an edit that names the other.
 */
export const CURATED_COLUMNS = [
  "key",
  "value",
  "unit",
  "scope",
  "category",
  "quote",
  "confidence",
  "page",
  "sheet",
  "cell",
  "sourceFileName",
  "sourcePointId",
  "metadata",
];

/** FoundationDataPointDto field -> the curated field it becomes. */
const FROM_DTO = {
  key: "dataKey",
  value: "dataValue",
  unit: "unit",
  scope: "scope",
  category: "category",
  quote: "sourceQuote",
  confidence: "confidence",
  page: "sourcePage",
  sheet: "sourceSheet",
  cell: "sourceCell",
  sourceFileName: "sourceFileName",
  sourcePointId: "id",
  metadata: "metadata",
};

/**
 * One extracted row as a curation row.
 *
 * Every column is present on every row, even when null. Two reasons, both about what the user and the
 * agent see: the frame's columns are then the same set regardless of which rows happen to carry a citation
 * (pandas builds columns from the union of keys, so a sparse first row would otherwise change the grid),
 * and the agent sees the full destination shape in the data it starts from rather than having to infer the
 * fields it is allowed to fill.
 */
export function toCuratedRow(row) {
  if (!row) return null;
  const out = {};
  CURATED_COLUMNS.forEach((column) => {
    const value = row[FROM_DTO[column]];
    out[column] = value === undefined ? null : value;
  });
  return out;
}

/** The rows to push into a session, skipping anything unusable. */
export function toCuratedRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(toCuratedRow).filter((row) => row !== null);
}
